/**
 * Adjustments view (legacy app-table key: "transactions" — Postgres table is
 * `adjustments`). Home for one-off cash movements that don't have their own
 * dedicated tab: Owner Contribution / Draw, Refund, Gift, Write-off, etc.
 */
import { DB } from "../lib/db";
import { CATEGORY_TO_GAME } from "../lib/catalogResolve";
import { normalizeSetName } from "../lib/setNames";
import { requestEnrichment } from "../lib/enrich";
import { slicePage, paginationControlsHtml } from "../utils/pagination";
import { icon } from "../utils/icon";
import { createRowSelection } from "../components/rowSelection";
import { escapeHtml, toast, scrollItemsToBottom, $ } from "../lib/dom";
import { fmt$, fmt$0, fmtN, fmtDate, sum, kpiBar, todayISO } from "../utils/format";
import { GAMES } from "../utils/pills";
import { openModal, closeModal, markModalDirty } from "../components/modal";
import { formField, readForm } from "../components/formField";
import { wireCombobox } from "../components/combobox";
import { wireLotSearch } from "../components/lotSearch";
import { SetCache } from "../lib/sets";
import { quickCardLookup } from "../components/quickCardLookup";
import { openSealedPicker } from "../components/sealedProductPicker";
import { nextId } from "../utils/ids";
import { render } from "../state";
import { type InvEffect } from "../lib/inventoryEffects";
import { downloadCsv } from "../lib/csv";
import { ownerLoanBalance, ownerLoanContributions, ownerLoanRepayments, cashOnHand } from "../lib/derived";
import "../lib/collectrImport";
import { endEbayListingsForLots } from "../lib/ebayDelist";
import { classForCondition } from "../lib/slab";

const sel = createRowSelection("adj");
function adjEdit(id: string): void { (window as any).editTxn?.(id); }
function adjDelete(id: string): void { (window as any).deleteRow?.("transactions", "Txn ID", id); }

const ADJUSTMENT_TYPES = ["Owner Contribution", "Owner Cash Contribution", "Owner Repayment", "Owner Draw", "Adjustment", "Refund", "Gift", "Write-off", "Other"];
const GIFT_TAX_CATEGORIES = ["Promotional / Prize", "Business gift (individual)", "Personal"];

/**
 * Items behavior per adjustment Type:
 *   - "in"  → Owner Contribution: pick a Card/Sealed type, type in details, lot created on save.
 *   - "out" → Gift: pick from existing inventory via lot search, lot decrements on save.
 *   - "none" → no Items section.
 */
type ItemsMode = "in" | "out" | "none";
export function itemsModeForType(type: string | undefined): ItemsMode {
  if (type === "Owner Contribution") return "in";
  if (type === "Gift")               return "out";
  if (type === "Write-off")          return "out"; // damaged / lost / stolen inventory
  return "none";
}

/* In-flight items state for the open Adjustment form. The same TxnItem shape
   covers both contribution items (filled in by the user) and gift items
   (filled in by the lot picker). The `mode` field on TxnState is captured at
   open time so save() knows which direction to apply.
*/
interface TxnItem {
  "AsType": "Card" | "Sealed"; "Game": string;
  "Item": string; "Set": string; "Number": string;
  "Condition": string; "ProductType": string;
  "Qty": number; "UnitBasis": number; "ContribFMV"?: number;
  "Lot ID"?: string;
}
interface TxnState { "Items": TxnItem[]; "_isNew": boolean; mode: ItemsMode; }
function S(): TxnState | null { return (window as any)._txnState as TxnState | null; }

function txTypePill(t: string | undefined): string {
  const v = (t || "").toLowerCase();
  if (v.includes("sale"))     return "green";
  if (v.includes("purchase")) return "blue";
  if (v.includes("trade"))    return "violet";
  if (v.includes("rip"))      return "amber";
  if (v.includes("expense"))  return "red";
  return "gray";
}

export function viewTransactions(root: HTMLElement): void {
  const tx = DB.table("transactions").slice().reverse();

  // Gift basis totals, grouped by Tax Category — only shown if there's at least one Gift.
  const gifts = tx.filter(t => t["Type"] === "Gift");
  const giftByCat: Record<string, number> = {};
  let giftBasisTotal = 0;
  for (const g of gifts) {
    const cat = (g["Tax Category"] || "Uncategorized") as string;
    const basis = Number(g["Basis Removed"]) || 0;
    giftByCat[cat] = (giftByCat[cat] || 0) + basis;
    giftBasisTotal += basis;
  }
  const giftSummary = gifts.length
    ? `<div class="panel p-3 mb-3 text-xs">
        <b class="text-emerald-300">Gifts &amp; prizes (basis removed):</b>
        ${Object.entries(giftByCat).map(([cat, v]) =>
          `<span class="ml-3"><span class="text-[var(--muted)]">${escapeHtml(cat)}</span> <b class="stat-num">${fmt$(v)}</b></span>`
        ).join("")}
        <span class="ml-3"><span class="text-[var(--muted)]">Total</span> <b class="stat-num">${fmt$(giftBasisTotal)}</b></span>
        <span class="ml-3 text-[var(--muted)]">· per-category figures help your accountant separate promotional / business-gift / personal at tax time</span>
       </div>`
    : "";

  const tx_total = tx.length;
  const tx_page = slicePage("adjustments", tx, 50);
  root.innerHTML = `
    ${kpiBar([
      { label: "Adjustments", value: fmtN(tx.length) },
      { label: "Cash In",     value: fmt$0(sum(tx, r => r["Cash In"])) },
      { label: "Cash Out",    value: fmt$0(sum(tx, r => r["Cash Out"])) },
      { label: "Net",         value: fmt$0(sum(tx, r => r["Cash In"]) - sum(tx, r => r["Cash Out"])), tone: (sum(tx, r => r["Cash In"]) - sum(tx, r => r["Cash Out"])) >= 0 ? "green" : "red" },
    ])}
    <div class="panel p-3 mb-4 flex flex-wrap items-center gap-x-6 gap-y-2">
      <div>
        <div class="text-xs text-[var(--muted)] uppercase tracking-wider">Cash on Hand</div>
        <div class="stat-num text-2xl font-bold ${cashOnHand() >= 0 ? 'text-emerald-300' : 'text-rose-300'}">${fmt$(cashOnHand())}</div>
      </div>
      <div>
        <div class="text-xs text-[var(--muted)] uppercase tracking-wider">Owed to Owner</div>
        <div class="stat-num text-2xl font-bold ${ownerLoanBalance() > 0.005 ? 'text-amber-300' : 'text-emerald-300'}">${fmt$(ownerLoanBalance())}</div>
      </div>
      <div class="text-xs text-[var(--muted)] leading-relaxed">
        Contributed (inventory value + cash in): <b class="text-emerald-300">${fmt$(ownerLoanContributions())}</b><br>
        Repaid to you: <b class="text-rose-300">${fmt$(ownerLoanRepayments())}</b>
      </div>
      <div class="ml-auto flex flex-wrap gap-2">
        <button class="btn btn-ghost !py-1 !px-3 text-xs" onclick="editOwnerCashContribution()" title="Record cash you put into the business">+ Add Cash Contribution</button>
        <button class="btn btn-primary !py-1 !px-3 text-xs" onclick="editOwnerRepayment()" title="Record a cash repayment to yourself">+ Record Repayment</button>
      </div>
    </div>
    ${giftSummary}
    <div class="panel p-3 mb-3 text-xs text-[var(--muted)]">
      For one-off cash movements and inventory adjustments: <b>Owner Contribution</b> brings cards/sealed into inventory; <b>Gift</b> sends them out as a prize or giveaway; <b>Write-off</b> removes damaged / lost / stolen inventory and books the basis as a loss. Cash-only types: <b>Owner Draw</b>, <b>Refund</b>, etc.
    </div>
    <div class="panel p-3 mb-3 flex flex-wrap gap-2 items-center">
      <button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="exportAdjustmentsCsv()" title="Download all adjustments as a CSV (raw values)">${icon("download")} CSV</button>
      ${sel.toolbarHtml([
        { id: "edit",   label: `${icon("edit")} Edit`,   title: "Edit selected row", handler: adjEdit },
        { id: "delete", label: `${icon("trash")} Delete`, title: "Delete selected row", danger: true, handler: adjDelete },
      ])}
    </div>
    <div class="panel scrollx">
      <table class="t">
        <thead><tr>
          <th>Adj ID</th><th>Date</th><th>Type</th><th>Related</th><th>Description</th><th>Items</th>
          <th class="text-right">In</th><th class="text-right">Out</th><th class="text-right">Fees</th>
          <th class="text-right">Basis Rem.</th>
        </tr></thead>
        <tbody>
          ${tx_page.map(t => {
            const id = escapeHtml(t["Txn ID"] || "");
            const items: any[] = Array.isArray(t.Items) ? t.Items : [];
            const _ra = sel.rowAttrs(id, `editTxn('${id}')`);
            const itemQty = items.reduce((s, x) => s + (Number(x.Qty) || 1), 0);
            const itemsSummary = items.length === 1
              ? escapeHtml((items[0].Item || "?") + (itemQty > 1 ? ` ×${itemQty}` : ""))
              : items.length
                ? `<b>${items.length}</b> items <span class="text-[var(--muted)]">(${itemQty} units)</span>`
                : `<span class="text-[var(--muted)]">—</span>`;
            return `<tr ${_ra}>
              <td class="font-mono text-xs"><a class="text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${id}')">${id}</a></td>
              <td>${escapeHtml(fmtDate(t["Date"]))}</td>
              <td><span class="pill pill-${txTypePill(t["Type"])}">${escapeHtml(t["Type"] || "")}</span></td>
              <td class="font-mono text-xs">${t["Related Lot ID"] ? `<a class="text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${escapeHtml(t["Related Lot ID"])}')">${escapeHtml(t["Related Lot ID"])}</a>` : ""}</td>
              <td class="text-xs" title="${escapeHtml(t["Description"] || "")}">${escapeHtml(t["Description"] || "")}</td>
              <td class="text-xs">${itemsSummary}</td>
              <td class="text-right stat-num">${fmt$(t["Cash In"])}</td>
              <td class="text-right stat-num">${fmt$(t["Cash Out"])}</td>
              <td class="text-right stat-num">${fmt$(t["Fees"])}</td>
              <td class="text-right stat-num">${fmt$(t["Basis Removed"])}</td>
            </tr>`;
          }).join("") || `<tr><td colspan="10" class="text-center text-[var(--muted)] py-8">No transactions.</td></tr>`}
        </tbody>
      </table>
    </div>
    ${paginationControlsHtml("adjustments", tx_total, 50)}
  `;
  sel.applyUI();
}

/* ============================================================
   Adjustment form (editTxn / saveTxn).
     • Owner Contribution → items-in: rows let you type Card/Sealed details,
       lots are created on save.
     • Gift → items-out: rows are a lot search picker; selected lots are
       decremented on save (like a Sale). A Tax Category dropdown also
       shows so year-end totals can be sorted Promotional / Business gift /
       Personal. Basis Removed auto-fills from picked lots.
     • Other types → no items section.
   ============================================================ */
export async function editTxn(id?: string, presetType?: string): Promise<void> {
  const isNew = !id;
  // Pull lot-bearing tables fresh so the contributed-item / lot picker
  // reflects cloud-current values. Also refresh transactions on edit.
  {
    const refreshes: Promise<void>[] = [DB.refreshTable("sealed"), DB.refreshTable("cards")];
    if (!isNew) refreshes.push(DB.refreshTable("transactions"));
    await Promise.all(refreshes);
  }
  const row = isNew
    ? { "Txn ID": nextId("TXN", "transactions", "Txn ID"), "Date": todayISO(), "Type": presetType || "Owner Contribution", "Items": [] }
    : DB.table("transactions").find(r => r["Txn ID"] === id);
  if (!row) { toast("Adjustment not found", "err"); return; }

  const mode: ItemsMode = itemsModeForType(row["Type"]);
  (window as any)._txnState = {
    "Items": (row.Items || []).map((x: any) => ({ ...x })),
    "_isNew": isNew,
    mode,
  } as TxnState;

  const isGift = row["Type"] === "Gift";
  const body = `
    <form id="txnForm" onsubmit="return false;">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        ${formField({ label: "Adjustment ID", name: "Txn ID", value: row["Txn ID"], required: true })}
        ${formField({ label: "Date", name: "Date", type: "date", value: fmtDate(row["Date"]), required: true })}
        ${formField({ label: "Type", name: "Type", value: row["Type"], options: ADJUSTMENT_TYPES, required: true })}
        <div id="txnTaxCategoryField" class="${isGift ? "" : "hidden"}">
          ${formField({ label: "Tax Category (Gifts)", name: "Tax Category", value: row["Tax Category"], options: GIFT_TAX_CATEGORIES, hint: "Promotional / Prize is generally a deductible advertising expense at cost basis. Business gift = $25/recipient/year cap. Personal = not deductible (consult your tax advisor)." })}
        </div>
        ${relatedLotPickerField(row["Related Lot ID"] || "")}
        <div class="sm:col-span-2">${formField({ label: "Description", name: "Description", value: row["Description"] })}</div>
        ${formField({ label: "Cash In ($)", name: "Cash In", type: "number", value: row["Cash In"], hint: (row["Type"] === "Owner Contribution" || row["Type"] === "Owner Cash Contribution") ? "Cash you put in — counts toward what you're owed." : "" })}
        ${formField({ label: "Cash Out ($)", name: "Cash Out", type: "number", value: row["Cash Out"], hint: row["Type"] === "Owner Repayment" ? `Outstanding owed to owner: ${fmt$(ownerLoanBalance())}. This repayment reduces it.` : "" })}
        ${formField({ label: "Fees ($)", name: "Fees", type: "number", value: row["Fees"] })}
        ${formField({ label: "Basis Removed ($)", name: "Basis Removed", type: "number", value: row["Basis Removed"], hint: itemsModeForType(row["Type"]) === "out" ? "Auto-filled from the picked lots on save." : "" })}
        <div class="sm:col-span-2">${formField({ label: "Notes", name: "Notes", type: "textarea", value: row["Notes"] })}</div>
      </div>

      <div id="txnItemsPanel" class="panel-2 p-3 mt-3 ${mode === "none" ? "hidden" : ""}">
        <div class="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h4 class="font-semibold text-emerald-300" id="txnItemsHeader">${headerForMode(mode)}</h4>
          <div class="flex gap-2 flex-wrap" id="txnItemsAddBtns">${addButtonsForMode(mode)}</div>
        </div>
        <div class="text-xs text-[var(--muted)] mb-2" id="txnItemsHint">${hintForMode(mode, isNew)}</div>
        <div id="txnItems" class="space-y-2 max-h-[40vh] overflow-y-auto pr-1"></div>
      </div>
    </form>`;
  const safeId = escapeHtml(row["Txn ID"]);
  const footer = `
    ${!isNew ? `<button class="btn btn-danger" onclick="deleteRow('transactions','Txn ID','${safeId}')">Delete</button>` : ""}
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="saveTxn('${isNew ? "" : safeId}')">${isNew ? "Add" : "Save"}</button>`;
  openModal({ title: isNew ? "Add Adjustment" : "Edit Adjustment", body, footer, wide: true });

  // Wire the Related Lot ID picker (combobox over card + sealed inventory)
  wireRelatedLotPicker();

  // Toggle items panel + tax category field as Type changes; reset items[] when switching modes.
  const typeSel = document.querySelector<HTMLSelectElement>('#txnForm [name="Type"]');
  typeSel?.addEventListener("change", () => {
    const newMode = itemsModeForType(typeSel.value);
    const isNowGift = typeSel.value === "Gift";
    const s = S();
    if (s && s.mode !== newMode) {
      // Items shape differs between in/out — wipe so we don't carry stale rows from the wrong mode.
      s.Items = [];
      s.mode = newMode;
    }
    document.getElementById("txnItemsPanel")?.classList.toggle("hidden", newMode === "none");
    document.getElementById("txnTaxCategoryField")?.classList.toggle("hidden", !isNowGift);
    const hdr = document.getElementById("txnItemsHeader"); if (hdr) hdr.textContent = headerForMode(newMode);
    const btns = document.getElementById("txnItemsAddBtns"); if (btns) btns.innerHTML = addButtonsForMode(newMode);
    const hint = document.getElementById("txnItemsHint"); if (hint) hint.innerHTML = hintForMode(newMode, isNew);
    renderTxnItemsList();
    markModalDirty();
  });
  renderTxnItemsList();
}

/* ----- Related Lot ID picker (combobox over user's card + sealed lots) ----- */
function lotDisplayString(lotId: string): string {
  if (!lotId) return "";
  const sealed = DB.table("sealed").find(r => r["Lot ID"] === lotId);
  if (sealed) return `${lotId} · ${sealed["Product"] || ""}`.trim().replace(/ ·\s*$/, "");
  const card = DB.table("cards").find(r => r["Card Lot ID"] === lotId);
  if (card) {
    const name = card["Card Name"] || "";
    const setName = card["Set"] || "";
    return `${lotId} · ${name}${setName ? ` · ${setName}` : ""}`.trim().replace(/ ·\s*$/, "");
  }
  // Unknown lot — show the raw ID (could be stale or non-inventory reference)
  return lotId;
}

function relatedLotPickerField(value: string): string {
  const display = lotDisplayString(value);
  return `<div>
    <label class="label" for="f_Related_Lot_ID_search">Related Lot ID</label>
    <div class="combobox-wrapper" data-related-lot-picker>
      <input class="input pr-8" id="f_Related_Lot_ID_search" type="text" autocomplete="off"
             placeholder="Search card or sealed inventory…"
             value="${escapeHtml(display)}">
      <button type="button" id="txnRelatedLotClear"
              class="absolute right-2 top-[10px] text-[var(--muted)] hover:text-white text-sm leading-none z-10 ${value ? "" : "hidden"}"
              onmousedown="event.preventDefault()" title="Clear">✕</button>
      <div class="combobox-list"></div>
    </div>
    <input type="hidden" name="Related Lot ID" value="${escapeHtml(value || "")}">
    <div class="text-xs text-[var(--muted)] mt-1">Optional — link to a sealed/card lot if this adjustment relates to one.</div>
  </div>`;
}

/** Call after openModal — attaches lot-search behavior + clear button to the Related Lot ID field. */
function wireRelatedLotPicker(): void {
  const wrapper = document.querySelector<HTMLElement>('#txnForm [data-related-lot-picker]');
  if (!wrapper) return;
  const visible = wrapper.querySelector<HTMLInputElement>("input")!;
  const hidden  = document.querySelector<HTMLInputElement>('#txnForm input[type="hidden"][name="Related Lot ID"]');
  const clearBtn = document.getElementById("txnRelatedLotClear");

  wireLotSearch(wrapper, {
    onSelect: (lot) => {
      if (hidden) hidden.value = lot.id;
      // Strip the "— qty N" suffix from the lot's label
      visible.value = (lot.label.split(" — ")[0] || lot.id).trim();
      clearBtn?.classList.remove("hidden");
      markModalDirty();
    },
  });

  clearBtn?.addEventListener("click", () => {
    if (hidden) hidden.value = "";
    visible.value = "";
    clearBtn.classList.add("hidden");
    markModalDirty();
  });
}

function headerForMode(m: ItemsMode): string {
  if (m === "in")  return "Contributed items (each becomes a new lot on save)";
  if (m === "out") return "Items leaving inventory (each decrements its source lot on save)";
  return "";
}
function hintForMode(m: ItemsMode, isNew: boolean): string {
  if (m === "in") {
    return `Type in the cards or sealed product you're putting into inventory. Each row creates a lot tagged with this adjustment's ID. Unit Basis is the value you're crediting per unit.${!isNew ? " <span class='text-amber-300'>Editing won't re-create lots.</span>" : ""}`;
  }
  if (m === "out") {
    return `Pick existing lots from your inventory — each one decrements on save (like a sale, just with no money in). Works for <b>Gift</b> (prize / giveaway) and <b>Write-off</b> (damaged / lost / stolen). The total cost basis flows into <b>Basis Removed</b> for tax tracking.${!isNew ? " <span class='text-amber-300'>Editing won't re-decrement lots.</span>" : ""}`;
  }
  return "";
}
function addButtonsForMode(m: ItemsMode): string {
  if (m === "in") {
    return `<button type="button" class="btn btn-ghost !py-1 !px-3 text-xs" onclick="triggerCollectrImport('adjustment-in')" title="Import contribution items from a Collectr portfolio CSV. Market Price becomes ContribFMV (what you'\''re owed); Average Cost Paid becomes Unit Basis (for COGS).">${icon("upload")} Import CSV</button>
            <button type="button" class="btn btn-primary !py-1 !px-3 text-xs" onclick="addTxnItem('Card')">+ Card</button>
            <button type="button" class="btn btn-primary !py-1 !px-3 text-xs" onclick="addTxnItem('Sealed')">+ Sealed</button>`;
  }
  if (m === "out") {
    return `<button type="button" class="btn btn-primary !py-1 !px-3 text-xs" onclick="addTxnItem()">+ Item</button>`;
  }
  return "";
}

export function addTxnItem(asType?: "Card" | "Sealed"): void {
  const s = S(); if (!s) return;
  s.Items.push({
    "AsType": asType || "Card", "Game": "Pokemon",
    "Item": "", "Set": "", "Number": "", "Condition": "Near Mint", "ProductType": "",
    "Qty": 1, "UnitBasis": 0, "ContribFMV": 0,
  });
  renderTxnItemsList(); markModalDirty();
  scrollItemsToBottom("txnItems");
}
export function removeTxnItem(idx: number): void {
  const s = S(); if (!s) return;
  s.Items.splice(idx, 1);
  renderTxnItemsList(); markModalDirty();
}
export function updateTxnItemField(idx: number, field: string, value: string): void {
  const s = S(); if (!s) return;
  const it = s.Items[idx]; if (!it) return;
  (it as any)[field] = (field === "Qty" || field === "UnitBasis" || field === "ContribFMV") ? (Number(value) || 0) : value;
  markModalDirty();
}

function renderTxnItemsList(): void {
  const container = document.getElementById("txnItems");
  if (!container) return;
  const s = S(); if (!s) return;
  if (s.mode === "in")  return renderTxnInItems(container, s.Items);
  if (s.mode === "out") return renderTxnOutItems(container, s.Items);
  container.innerHTML = "";
}

/** Live "you'll be owed" total for the contribution items (frozen value × qty). */
function txnInOwedSummary(items: TxnItem[]): string {
  if (!items.length) return "";
  const invOwed = items.reduce((a, it) => {
    const per = (Number(it.ContribFMV) || Number(it.UnitBasis) || 0);
    return a + per * (Number(it.Qty) || 1);
  }, 0);
  return `<div class="text-xs text-[var(--muted)] mt-2 pt-2 border-t border-[var(--border)]">Inventory value you'll be owed: <b class="text-amber-300">${fmt$(invOwed)}</b> <span>(market value \u00d7 qty, frozen when you save; any Cash In adds on top)</span></div>`;
}

/* ----- IN mode (Owner Contribution) — user types the details ----- */
function renderTxnInItems(container: HTMLElement, items: TxnItem[]): void {
  if (!items.length) {
    container.innerHTML = `<div class="text-sm text-[var(--muted)] italic py-2">No items yet — click "+ Card" or "+ Sealed".</div>`;
    return;
  }
  container.innerHTML = items.map((item, i) => {
    const isCard = item.AsType === "Card";
    const typeBadge = isCard ? `<span class="pill pill-violet">Card</span>` : `<span class="pill pill-blue">Sealed</span>`;
    return `
      <div class="panel p-2" data-txn-idx="${i}">
        <div class="flex items-center gap-2 mb-2 flex-wrap">
          ${typeBadge}
          <select class="select !py-1 text-sm w-auto" onchange="updateTxnItemField(${i},'Game',this.value)">
            ${GAMES.map(g => `<option value="${escapeHtml(g)}" ${item.Game === g ? "selected" : ""}>${escapeHtml(g)}</option>`).join("")}
          </select>
          <button type="button" class="btn btn-ghost !py-1 !px-2 text-xs" onclick="${isCard ? `lookupCardForTxnItem(${i})` : `lookupSealedForTxnItem(${i})`}" title="Find on ${isCard ? "the Pokémon TCG API" : "the sealed product catalog"}">⌕ Lookup</button>
          <button type="button" class="btn btn-ghost !py-1 !px-2 text-xs ml-auto" onclick="removeTxnItem(${i})" title="Remove">✕</button>
        </div>
        <div class="grid grid-cols-12 gap-2">
          <div class="col-span-12 sm:col-span-5">
            <label class="label text-xs">${isCard ? "Card Name" : "Product Name"}</label>
            <input class="input" placeholder="${isCard ? 'e.g. Charizard ex' : 'e.g. Surging Sparks Booster Box'}"
                   value="${escapeHtml(item.Item || "")}"
                   oninput="updateTxnItemField(${i},'Item',this.value)">
          </div>
          <div class="col-span-8 sm:col-span-3">
            <label class="label text-xs">Set</label>
            <div class="combobox-wrapper" data-txn-set="${i}">
              <input class="input" autocomplete="off" placeholder="Type to search…"
                     value="${escapeHtml(item.Set || "")}"
                     oninput="updateTxnItemField(${i},'Set',this.value)">
              <div class="combobox-list"></div>
            </div>
          </div>
          <div class="col-span-4 sm:col-span-2">
            <label class="label text-xs">${isCard ? "Card #" : "Type"}</label>
            <input class="input" placeholder="${isCard ? '#' : 'e.g. ETB'}"
                   value="${escapeHtml(isCard ? (item.Number || "") : (item.ProductType || ""))}"
                   oninput="updateTxnItemField(${i},'${isCard ? 'Number' : 'ProductType'}',this.value)">
          </div>
          ${isCard ? `
          <div class="col-span-12 sm:col-span-2">
            <label class="label text-xs">Condition</label>
            <select class="select" onchange="updateTxnItemField(${i},'Condition',this.value)">
              ${["Near Mint", "Mint", "Lightly Played", "Moderately Played", "Heavily Played", "Damaged", "PSA 10", "PSA 9", "BGS 10", "CGC 10", "Other"].map(o => `<option ${item.Condition === o ? "selected" : ""}>${escapeHtml(o)}</option>`).join("")}
            </select>
          </div>` : `<div class="col-span-12 sm:col-span-2"></div>`}
          <div class="col-span-3 sm:col-span-1">
            <label class="label text-xs">Qty</label>
            <input class="input" type="number" step="1" min="1" value="${item.Qty || 1}" oninput="updateTxnItemField(${i},'Qty',this.value)">
          </div>
          <div class="col-span-4 sm:col-span-2">
            <label class="label text-xs">Market value ($/ea)</label>
            <input class="input" type="number" step="0.01" value="${item.ContribFMV || 0}" oninput="updateTxnItemField(${i},'ContribFMV',this.value)" title="What you're owed per unit \u2014 frozen at today's value.">
          </div>
          <div class="col-span-5 sm:col-span-2">
            <label class="label text-xs">Cost basis ($/ea)</label>
            <input class="input" type="number" step="0.01" value="${item.UnitBasis || 0}" placeholder="defaults to market" oninput="updateTxnItemField(${i},'UnitBasis',this.value)" title="Used for COGS when sold. Leave 0 to default to market value.">
          </div>
        </div>
      </div>`;
  }).join("") + txnInOwedSummary(items);
  // Per-row Set combobox follows the row's Game
  items.forEach((_item, i) => {
    const wrapper = container.querySelector<HTMLElement>(`[data-txn-set="${i}"]`);
    if (!wrapper) return;
    const getGame = () => {
      const cur = S();
      return (cur?.Items[i]?.Game) || "Pokemon";
    };
    wireCombobox(wrapper, {
      getOptions: () => SetCache.get(getGame()),
      asyncLoader: () => SetCache.ensureFresh(getGame()),
      onSelect: (v) => updateTxnItemField(i, "Set", v),
    });
  });
}

/* ----- OUT mode (Gift) — pick existing lots; decrement on save ----- */
function renderTxnOutItems(container: HTMLElement, items: TxnItem[]): void {
  if (!items.length) {
    container.innerHTML = `<div class="text-sm text-[var(--muted)] italic py-2">No items yet — click "+ Item" and search for the lot you're giving away.</div>`;
    return;
  }
  container.innerHTML = items.map((item, i) => {
    const linked = !!item["Lot ID"];
    const lineBasis = (Number(item.UnitBasis) || 0) * (Number(item.Qty) || 1);
    return `
      <div class="panel p-2 grid grid-cols-12 gap-2 items-end" data-txn-out-idx="${i}">
        <div class="col-span-12 sm:col-span-6">
          <label class="label text-xs">Search inventory</label>
          <div class="combobox-wrapper" data-txn-out-search="${i}">
            <input class="input !py-1 text-sm" autocomplete="off" placeholder="Type product, card name, or lot ID…"
                   value="${escapeHtml(linked ? `${item["Lot ID"]} · ${item.Item || ""}`.trim() : (item.Item || ""))}">
            <div class="combobox-list"></div>
          </div>
          <div class="text-xs mt-1">${linked
            ? `<span class="text-[var(--muted)]">Lot:</span> <code>${escapeHtml(item["Lot ID"])}</code> <span class="pill pill-${item.AsType === "Card" ? "violet" : "blue"}">${escapeHtml(item.AsType || "")}</span>`
            : `<span class='text-amber-300'>No lot picked yet.</span>`}</div>
        </div>
        <div class="col-span-3 sm:col-span-1">
          <label class="label text-xs">Qty</label>
          <input class="input !py-1 text-sm" type="number" step="1" min="1" value="${item.Qty || 1}" oninput="updateTxnItemField(${i},'Qty',this.value)">
        </div>
        <div class="col-span-5 sm:col-span-2">
          <label class="label text-xs">Unit Basis</label>
          <input class="input !py-1 text-sm" type="number" step="0.01" value="${item.UnitBasis || 0}" oninput="updateTxnItemField(${i},'UnitBasis',this.value)">
        </div>
        <div class="col-span-3 sm:col-span-2 text-right">
          <div class="label text-xs">Line basis</div>
          <div class="stat-num">${fmt$(lineBasis)}</div>
        </div>
        <div class="col-span-1 flex justify-end items-end">
          <button type="button" class="btn btn-ghost !py-1 !px-2 text-xs" onclick="removeTxnItem(${i})" title="Remove">✕</button>
        </div>
      </div>`;
  }).join("");
  // Wire each row's lot search (any lot type, exclude already-picked)
  items.forEach((_item, i) => {
    const wrapper = container.querySelector<HTMLElement>(`[data-txn-out-search="${i}"]`);
    if (!wrapper) return;
    wireLotSearch(wrapper, {
      excludeIds: () => {
        const set = new Set<string>();
        const cur = S();
        (cur?.Items || []).forEach((x, j) => { if (j !== i && x["Lot ID"]) set.add(x["Lot ID"]!); });
        return set;
      },
      onSelect: (lot) => {
        const cur = S(); if (!cur) return;
        const it = cur.Items[i];
        it["Lot ID"] = lot.id;
        it["AsType"] = lot.type as "Card" | "Sealed";
        if (lot.type === "Sealed") {
          const src = DB.table("sealed").find(r => r["Lot ID"] === lot.id);
          if (src) {
            it["Item"] = src["Product"] || "";
            it["Set"]  = src["Set"] || "";
            it["Game"] = src["Game"] || "";
            it["ProductType"] = src["Product Type"] || "";
          }
        } else {
          const src = DB.table("cards").find(r => r["Card Lot ID"] === lot.id);
          if (src) {
            it["Item"]      = src["Card Name"] || "";
            it["Set"]       = src["Set"] || "";
            it["Number"]    = src["Card #"] || "";
            it["Condition"] = src["Condition/Grade"] || "";
            it["Game"]      = src["Game"] || "";
          }
        }
        it["UnitBasis"] = +Number(lot.basis).toFixed(2);
        renderTxnItemsList(); markModalDirty();
      },
    });
  });
}

/** Pokémon TCG card lookup for an Owner-Contribution (IN) card row. Fills
 *  name / set / number / game and prefills Market value ($/ea) from TCGplayer
 *  market price; leaves Cost basis alone. Mirrors the Purchase/Trade pickers. */
export function lookupCardForTxnItem(idx: number): void {
  const s = S(); if (!s) return;
  const item = s.Items[idx];
  if (!item || item.AsType !== "Card") return;
  (window as any)._lookupOnPick = (c: any) => {
    item.Item   = c.name || "";
    item.Set    = normalizeSetName((c.set && c.set.name) || item.Set || "");
    item.Number = c.number || "";
    item.Game   = c.game || item.Game || "Pokemon";
    const tcg = c.tcgplayer && c.tcgplayer.prices;
    if (tcg) {
      const p = Object.values<any>(tcg).find(x => x && typeof x.market === "number");
      if (p) item.ContribFMV = +Number(p.market).toFixed(2);
    }
    renderTxnItemsList(); markModalDirty();
    toast(`Picked: ${c.name}${c.set?.name ? " · " + c.set.name : ""}`);
    setTimeout(() => { delete (window as any)._lookupOnPick; }, 100);
  };
  const seed = (item.Item || "").trim();
  if (!seed) { toast("Type a card name (or 'name 123') first", "err"); return; }
  const narrow: { number?: string; set?: string; game?: string } = { game: item.Game || "Pokemon" };
  if (item.Number) narrow.number = item.Number.trim();
  if (item.Set)    narrow.set    = item.Set.trim();
  quickCardLookup(1, seed, narrow);
}

/** Sealed-catalog lookup for an Owner-Contribution (IN) sealed row. Fills
 *  product / set / type and prefills Market value ($/ea) from the TCGCSV
 *  current price; leaves Cost basis alone. */
export function lookupSealedForTxnItem(idx: number): void {
  const s = S(); if (!s) return;
  const item = s.Items[idx];
  if (!item || item.AsType !== "Sealed") return;
  (window as any)._sealedOnPick = (r: any) => {
    item.Item = r.name || "";
    item.Set  = normalizeSetName(r.group_name || item.Set || "");
    if (CATEGORY_TO_GAME[r.category_id]) item.Game = CATEGORY_TO_GAME[r.category_id];
    const ptMap: Record<string, string> = {
      box: "Booster Box", etb: "ETB", bundle: "Booster Bundle",
      pack: "Booster Pack", tin: "Tin", blister: "Blister", deck: "Deck",
      collection: "Collection Box", other: "Other",
    };
    if (r.product_kind && ptMap[r.product_kind]) item.ProductType = ptMap[r.product_kind];
    const price = r.market_price ?? r.mid_price ?? r.low_price ?? null;
    if (price != null) item.ContribFMV = +Number(price).toFixed(2);
    renderTxnItemsList(); markModalDirty();
    toast(`Picked: ${r.name}`);
    setTimeout(() => { delete (window as any)._sealedOnPick; }, 100);
  };
  openSealedPicker((item.Item || "").trim() || undefined);
}

export function saveTxn(origId: string): void {
  const d = readForm($("#txnForm"));
  const s = S() || { Items: [], _isNew: !origId, mode: itemsModeForType(d["Type"]) };
  const isNew = !origId;
  const type = d["Type"] || "";

  // Over-repayment guard: warn when a repayment exceeds what's still owed.
  if (type === "Owner Repayment") {
    const existing = !isNew ? DB.table("transactions").find(r => r["Txn ID"] === origId) : null;
    const prevOut = existing && existing["Type"] === "Owner Repayment" ? (Number(existing["Cash Out"]) || 0) : 0;
    const outstanding = +(ownerLoanBalance() + prevOut).toFixed(2);
    const out = Number(d["Cash Out"]) || 0;
    if (out > outstanding + 0.005) {
      if (!confirm(`This repayment (${fmt$(out)}) exceeds the outstanding owed-to-owner balance (${fmt$(outstanding)}) \u2014 the balance would go negative, and Books Health will warn.\n\nSave anyway?`)) return;
    }
  }
  const txnId = (d["Txn ID"] || nextId("TXN", "transactions", "Txn ID")).trim();
  const mode = itemsModeForType(type);

  // Only keep items whose mode matches the current Type (defensive — keeps stale rows out).
  let items: TxnItem[] = [];
  if (mode === "in") {
    items = s.Items.filter(x => x.Item && x.Item.trim());
  } else if (mode === "out") {
    items = s.Items.filter(x => !!x["Lot ID"]);
  }

  // Auto-fill Basis Removed for out-mode types (Gift / Write-off) unless overridden
  if (mode === "out") {
    const outBasis = items.reduce((acc, x) => acc + (Number(x.UnitBasis) || 0) * (Number(x.Qty) || 1), 0);
    // Respect the user's manually entered Basis Removed, but auto-fill on a fresh entry
    if (!d["Basis Removed"] || Number(d["Basis Removed"]) === 0) {
      d["Basis Removed"] = +outBasis.toFixed(2);
    }
  }

  // Apply inventory effects only on initial save. Every lot created or
  // decremented is recorded in a reversible Inv Effect footprint (parity with
  // sales/trades/grading/rips) so deleting the adjustment restores inventory.
  const effect: InvEffect = [];
  if (isNew && items.length) {
    const date = d["Date"] || todayISO();

    if (mode === "in") {
      // Owner Contribution — create new lots
      for (const it of items) {
        const qty = Number(it.Qty) || 1;
        const fmv = Number(it.ContribFMV) || 0;
        let unitBasis = Number(it.UnitBasis) || 0;
        if (!unitBasis && fmv) unitBasis = fmv;          // cost defaults to market value
        const lotFmv = fmv > 0 ? fmv : unitBasis;        // owed/market value snapshot
        // Persist the snapshot back onto the item so the owed balance stays frozen.
        it.UnitBasis = unitBasis;
        it.ContribFMV = lotFmv;
        const totalBasis = +(unitBasis * qty).toFixed(2);
        const totalFmv = +(lotFmv * qty).toFixed(2);
        const ugl = +(totalFmv - totalBasis).toFixed(2);
        if (it.AsType === "Sealed") {
          const newId = nextId("S", "sealed", "Lot ID");
          DB.add("sealed", {
            "Lot ID": newId, "Game": it.Game || "Pokemon",
            "Product": it.Item, "Set": normalizeSetName(it.Set), "Product Type": it.ProductType || "",
            "Inventory Class": "Long Term Hold", "Qty": qty,
            "Acq Date": date, "Acq Type": type, "Valuation Source": "Manual",
            "Unit Basis": unitBasis, "Total Basis": totalBasis,
            "Current FMV": totalFmv, "Unrealized G/L": ugl,
            "Source ID": txnId,
            "Notes": `From ${type.toLowerCase()} ${txnId}`,
          });
          it["Lot ID"] = newId;
          effect.push({ kind: "lotCreate", table: "sealed", lotId: newId });
        } else {
          const newId = nextId("C", "cards", "Card Lot ID");
          DB.add("cards", {
            "Card Lot ID": newId, "Game": it.Game || "Pokemon",
            "Card Name": it.Item, "Set": normalizeSetName(it.Set), "Card #": it.Number || "",
            "Inventory Class": classForCondition(String(it.Condition || "")), "Condition/Grade": it.Condition || "Near Mint",
            "Qty": qty, "Acq Date": date, "Acq Type": type,
            "Valuation Source": it.Game === "Magic: The Gathering" ? "Scryfall" : "TCGPlayer",
            "Unit Basis": unitBasis, "Total Basis": totalBasis,
            "Current FMV": totalFmv, "Unrealized G/L": ugl,
            "Status": "Available", "Source ID": txnId,
            "Notes": `From ${type.toLowerCase()} ${txnId}`,
          });
          it["Lot ID"] = newId;
          effect.push({ kind: "lotCreate", table: "cards", lotId: newId });
        }
      }
    } else if (mode === "out") {
      // Gift / Write-off — decrement source lots (like a Sale)
      const terminalStatus = type === "Write-off" ? "Written Off" : "Gifted";
      for (const it of items) {
        if (!it["Lot ID"]) continue;
        const qtyOut = Number(it.Qty) || 1;
        const sealed = DB.table("sealed").find(r => r["Lot ID"] === it["Lot ID"]);
        const card   = DB.table("cards").find(r  => r["Card Lot ID"] === it["Lot ID"]);
        if (sealed) {
          const curQty = Number(sealed["Qty"]) || 0;
          const oldQty = Math.max(curQty, 1);
          const newQty = Math.max(0, curQty - qtyOut);
          const oldFMV = Number(sealed["Current FMV"]) || 0;
          const newFMV = +(oldFMV / oldQty * newQty).toFixed(2);
          const statusBefore = sealed["Status"];
          const sealedPatch: Record<string, any> = {
            "Qty": newQty,
            "Total Basis": +((Number(sealed["Unit Basis"]) || 0) * newQty).toFixed(2),
            "Current FMV": newFMV,
          };
          let statusAfter: string | undefined;
          if (newQty === 0) { sealedPatch["Status"] = terminalStatus; statusAfter = terminalStatus; }
          DB.update("sealed", "Lot ID", it["Lot ID"]!, sealedPatch);
          effect.push({ kind: "lotDelta", table: "sealed", lotId: it["Lot ID"]!, dQty: newQty - curQty, dFMV: newFMV - oldFMV, statusBefore, statusAfter });
        } else if (card) {
          const curQty = Number(card["Qty"]) || 0;
          const oldQty = Math.max(curQty, 1);
          const newQty = Math.max(0, curQty - qtyOut);
          const oldFMV = Number(card["Current FMV"]) || 0;
          const newFMV = +(oldFMV / oldQty * newQty).toFixed(2);
          const statusBefore = card["Status"];
          const patch: Record<string, any> = {
            "Qty": newQty,
            "Total Basis": +((Number(card["Unit Basis"]) || 0) * newQty).toFixed(2),
            "Current FMV": newFMV,
          };
          let statusAfter: string | undefined;
          if (newQty === 0) { patch["Status"] = terminalStatus; statusAfter = terminalStatus; }
          DB.update("cards", "Card Lot ID", it["Lot ID"]!, patch);
          effect.push({ kind: "lotDelta", table: "cards", lotId: it["Lot ID"]!, dQty: newQty - curQty, dFMV: newFMV - oldFMV, statusBefore, statusAfter });
        }
      }
    }
  }

  const txnRow: Record<string, any> = { ...d, "Items": items };
  if (isNew) {
    txnRow["Inv Effect"] = effect;
  } else {
    // Edits never re-touch inventory — keep the original footprint intact.
    const existing = DB.table("transactions").find(r => r["Txn ID"] === origId);
    txnRow["Inv Effect"] = Array.isArray(existing?.["Inv Effect"]) ? existing!["Inv Effect"] : [];
  }
  if (origId) DB.update("transactions", "Txn ID", origId, txnRow);
  else        DB.add("transactions", txnRow);

  delete (window as any)._txnState;
  const msg = isNew && items.length
    ? (mode === "in"  ? `Adjustment saved · ${items.length} lot(s) created`
    :  mode === "out" ? `Gift saved · ${items.length} lot(s) decremented`
    :  "Transaction saved")
    : "Transaction saved";
  toast(msg);
  // eBay safety net: end live listings on card lots this adjustment removed
  // (gifts / write-offs decrement inventory just like sales do).
  {
    const depleted = effect
      .filter((e: any) => e.kind === "lotDelta" && (e.table === "cards" || e.table === "sealed") && (Number(e.dQty) || 0) < 0)
      .map((e: any) => String(e.lotId));
    if (depleted.length) void endEbayListingsForLots(depleted, `adjusted out (${txnId})`);
  }
  // Contribution items (incl. CSV imports) may lack catalog identity.
  requestEnrichment();
  closeModal();
  render();
}

/** Download all adjustments as a raw-value CSV. */
export function exportAdjustmentsCsv(): void {
  const rows = DB.table("transactions").slice().reverse();
  downloadCsv(`adjustments-${todayISO()}.csv`, rows, [
    { label: "Adj ID",         get: r => r["Txn ID"] },
    { label: "Date",           get: r => r["Date"] },
    { label: "Type",           get: r => r["Type"] },
    { label: "Tax Category",   get: r => r["Tax Category"] },
    { label: "Related Lot ID", get: r => r["Related Lot ID"] },
    { label: "Description",    get: r => r["Description"] },
    { label: "Items",          get: r => (Array.isArray(r.Items) ? r.Items.length : 0) },
    { label: "Cash In",        get: r => r["Cash In"] },
    { label: "Cash Out",       get: r => r["Cash Out"] },
    { label: "Fees",           get: r => r["Fees"] },
    { label: "Basis Removed",  get: r => r["Basis Removed"] },
    { label: "Notes",          get: r => r["Notes"] },
  ]);
}
(window as any).exportAdjustmentsCsv = exportAdjustmentsCsv;

/** Open a fresh Adjustment pre-set to Owner Repayment (from the balance panel). */
export function editOwnerRepayment(): void { editTxn(undefined, "Owner Repayment"); }
export function editOwnerCashContribution(): void { editTxn(undefined, "Owner Cash Contribution"); }

// Inline-handler bridges
(window as any).editTxn            = editTxn;
(window as any).editOwnerRepayment = editOwnerRepayment;
(window as any).editOwnerCashContribution = editOwnerCashContribution;
(window as any).saveTxn            = saveTxn;
(window as any).addTxnItem         = addTxnItem;
(window as any).renderTxnItemsList = renderTxnItemsList;
(window as any).removeTxnItem      = removeTxnItem;
(window as any).updateTxnItemField = updateTxnItemField;
(window as any).lookupCardForTxnItem   = lookupCardForTxnItem;
(window as any).lookupSealedForTxnItem = lookupSealedForTxnItem;
